import { Download, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";

const scopeContent = `
===============================================
  JAIN LEXICON — SCOPE OF WORK
===============================================

PROJECT OVERVIEW
----------------
A web-based dictionary/glossary platform for Jain terminology, providing meanings in English, Hindi, and Gujarati with pronunciation guides. Designed for accessibility with high-contrast UI targeting a 60+ age demographic.

Technology Stack: React, Vite, TypeScript, Tailwind CSS, Lovable Cloud (Supabase)

===============================================
PUBLIC-FACING MODULES
===============================================

1. HOMEPAGE
   - Hero section with scholarly credits
   - Quick Access navigation links
   - Alphabetical (A-Z) Index
   - Featured Terms showcase
   - Mission & Vision section
   - Contribute CTA section

2. BROWSE (/browse)
   - Alphabetical filtering via A-Z index
   - Paginated term listing
   - Responsive grid layout

3. SEARCH (/search)
   - Debounced autocomplete search
   - Search across word, English, Hindi, and Gujarati meanings
   - Instant results display

4. TERM DETAIL (/term/:id)
   - Full term view with pronunciation guide
   - Meanings displayed in 3 languages (English, Hindi, Gujarati)
   - Example usage section
   - Related terms sidebar (same starting letter)

5. CONTRIBUTE (/contribute)
   - Public submission form for new terms
   - Fields: Word, English/Hindi/Gujarati meanings, pronunciation, contributor name & email
   - Real-time duplicate detection (alerts if word exists in approved database)
   - Submission guidelines displayed

6. MULTI-LANGUAGE UI
   - Language context provider for interface translations
   - Supports multiple UI languages

===============================================
ADMIN PANEL (/admin)
===============================================

1. DASHBOARD
   - Statistics overview: Pending, Approved, Rejected counts
   - Quick action cards

2. PENDING REQUESTS
   - Review submitted terms
   - "Exists" column for duplicate detection (YES/NO badge)
   - Approve/Reject workflow with admin notes
   - Search and filter functionality

3. APPROVED TERMS
   - Manage all published terms
   - Edit/Delete capabilities
   - Direct term addition

4. REJECTED TERMS
   - View rejected submissions
   - Option to reconsider

5. GRAPHS & ANALYTICS
   - Visual analytics powered by Recharts
   - Submission trends and statistics

6. REPORTS
   - Contributor activity reports
   - Email display with mailto links for direct communication
   - Contribution counts per contributor

7. ADD DATA
   - Direct term entry interface for administrators

===============================================
BACKEND & INFRASTRUCTURE
===============================================

1. DATABASE
   - "terms" table: word, meaning_english, meaning_hindi, meaning_gujarati, pronunciation, example_usage, status, contributor_email, contributor_name, admin_notes, audio_url
   - "user_roles" table: role-based access (admin, moderator, user)

2. AUTHENTICATION
   - Email-based login for admin access
   - Role-based authorization
   - Session management with safety timeouts

3. SECURITY
   - Row Level Security (RLS) policies on all tables
   - Secure API access via anon key

4. STORAGE
   - "pronunciation-audio" bucket for audio file storage

===============================================
UX & ACCESSIBILITY
===============================================

- High-contrast navy blue & gold theme
- Inter font family, 24px base size, line-height 1.9
- Extra-bold headings for maximum readability
- Mobile-optimized layout (390x844 viewport)
- Smooth scrolling with auto scroll-to-top on navigation
- Animated sections using Framer Motion
- Programmatic navigation for instant response

===============================================
POTENTIAL FUTURE ENHANCEMENTS
===============================================

1. Audio pronunciation playback for terms
2. Bulk import/export of terms (CSV/Excel)
3. User accounts for contributors to track submission status
4. Bookmarking/favorites for logged-in users
5. Multi-admin moderation workflow with notes
6. PWA support for offline access
7. Advanced search filters (by language, category, date)
8. Email notifications to contributors on approval/rejection

===============================================
Document Generated: ${new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}
Project: Jain Lexicon
===============================================
`;

const ScopeOfWork = () => {
  const handleDownload = () => {
    const blob = new Blob([scopeContent], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "Jain_Lexicon_Scope_of_Work.txt";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-background min-h-screen py-12">
      <div className="container max-w-4xl">
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-3">
            <FileText className="text-primary h-8 w-8 shrink-0" />
            <h1 className="text-foreground font-serif text-2xl font-bold break-words sm:text-3xl">
              Scope of Work
            </h1>
          </div>
          <Button onClick={handleDownload} className="shrink-0 gap-2">
            <Download className="h-4 w-4" />
            Download
          </Button>
        </div>

        <div className="bg-card border-border rounded-2xl border p-4 sm:p-6 md:p-10">
          <pre className="text-foreground overflow-x-auto whitespace-pre-wrap break-words font-mono text-xs leading-relaxed sm:text-sm md:text-base">
            {scopeContent}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default ScopeOfWork;
